import React, { useState, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useEmployerJobPostings, useEmployeeJobMatches, useDeleteJobPosting } from '../../hooks/useJobs';
import { formatJobTitle, getJobStatusLabel, getJobStatusColor } from '../../types/job';
import './JobHistory.css';

const JobHistory = () => {
  const { user } = useAuth();
  const userRole = user?.role_name;
  const userId = user?.id;
  const [filter, setFilter] = useState('all');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  
  const deleteJobPostingMutation = useDeleteJobPosting();

  // Fetch data based on user role
  const { data: employerJobs, isLoading: isLoadingEmployerJobs } = useEmployerJobPostings(
    userRole === 'employer' ? userId : null,
    { enabled: userRole === 'employer' && !!userId }
  );

  const { data: employeeMatches, isLoading: isLoadingEmployeeMatches } = useEmployeeJobMatches({
    enabled: userRole === 'employee' && !!userId
  });

  const isLoading = userRole === 'employer' ? isLoadingEmployerJobs : isLoadingEmployeeMatches;

  // Transform API data to match expected format
  const jobs = useMemo(() => {
    if (userRole === 'employer' && employerJobs) {
      // For employers: transform job postings to job history format
      return employerJobs.map(job => ({
        id: job.id,
        job_date: job.date || job.job_date,
        start_time: job.start_time,
        end_time: job.end_time,
        total_hours: job.number_of_hours || job.total_hours,
        status: job.status,
        job_role: job.job_role_detail?.job_role_name || job.job_role?.job_role_name || job.job_role_name || job.job_role,
        practice_address: job.employer_address || job.employer?.address || 'N/A',
        employer_name: job.employer_name || (job.employer?.first_name && job.employer?.last_name ? `${job.employer.first_name} ${job.employer.last_name}` : 'N/A'),
        created_at: job.created_at,
        // For employers, we might want to show match info if available
        match: job.matches && job.matches.length > 0 ? {
          employee_name: job.matches[0]?.employee_detail?.first_name && job.matches[0]?.employee_detail?.last_name
            ? `${job.matches[0].employee_detail.first_name} ${job.matches[0].employee_detail.last_name}`
            : job.matches[0]?.employee_name || 'N/A',
          distance_miles: job.matches[0]?.distance_miles,
          employee_status: job.matches[0]?.status
        } : null
      }));
    } else if (userRole === 'employee' && employeeMatches) {
      // For employees: transform job matches to job history format
      return employeeMatches
        .filter(match => match.status === 'accepted' || match.status === 'pending') // Only show accepted/pending matches
        .map(match => ({
          id: match.job_posting_detail?.id || match.job_posting,
          job_date: match.job_posting_detail?.date || match.job?.job_date,
          start_time: match.job_posting_detail?.start_time || match.job?.start_time,
          end_time: match.job_posting_detail?.end_time || match.job?.end_time,
          total_hours: match.job_posting_detail?.number_of_hours || match.job?.total_hours,
          status: match.job_posting_detail?.status === 'done' ? 'completed' : 
                  match.job_posting_detail?.status === 'accepted' ? 'accepted' : 
                  match.status === 'accepted' ? 'accepted' : 'pending',
          job_role: match.job_posting_detail?.job_role_detail?.job_role_name || match.job?.job_role,
          practice_address: match.job_posting_detail?.employer_address || match.job?.practice_address,
          employer_name: match.job_posting_detail?.employer_name || match.job?.employer_name,
          created_at: match.created_at,
          match: {
            id: match.id,
            employee_name: match.employee_detail?.first_name && match.employee_detail?.last_name
              ? `${match.employee_detail.first_name} ${match.employee_detail.last_name}`
              : match.employee_name || 'N/A',
            distance_miles: match.distance_miles,
            employee_status: match.status
          }
        }));
    }
    return [];
  }, [employerJobs, employeeMatches, userRole]);

  // Filter jobs based on selected filter
  const filteredJobs = useMemo(() => {
    if (filter === 'all') return jobs;
    
    return jobs.filter(job => {
      // Map filter to backend status
      if (filter === 'open') {
        return job.status === 'open';
      }
      if (filter === 'match_found') {
        return job.status === 'match_found';
      }
      if (filter === 'accepted') {
        // For employees, check if match is accepted
        if (userRole === 'employee' && job.match) {
          return job.match.employee_status === 'accepted' || job.status === 'accepted';
        }
        // For employers, check job status
        return job.status === 'accepted';
      }
      if (filter === 'done') {
        return job.status === 'done' || job.status === 'completed';
      }
      // For other filters, match status directly
      return job.status === filter;
    });
  }, [jobs, filter, userRole]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short',
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const handleDeleteClick = (jobId) => {
    setDeleteConfirmId(jobId);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmId) {
      try {
        await deleteJobPostingMutation.mutateAsync(deleteConfirmId);
        setDeleteConfirmId(null);
      } catch (error) {
        // Error is handled by the mutation's onError
        console.error('Delete error:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  // Check if job can be deleted (only open jobs can be deleted)
  const canDeleteJob = (job) => {
    if (userRole !== 'employer') return false;
    // Only allow deletion of jobs that are open or pending (not accepted/completed)
    return job.status === 'open' || job.status === 'pending' || job.status === 'match_found';
  };

  return (
    <div className="job-history">
      <div className="job-history__header">
        <h2 className="job-history__title">Job History</h2>
        <div className="job-history__filters">
          <button
            className={`job-history__filter ${filter === 'all' ? 'job-history__filter--active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`job-history__filter ${filter === 'open' ? 'job-history__filter--active' : ''}`}
            onClick={() => setFilter('open')}
          >
            Open
          </button>
          <button
            className={`job-history__filter ${filter === 'match_found' ? 'job-history__filter--active' : ''}`}
            onClick={() => setFilter('match_found')}
          >
            Match Found
          </button>
          <button
            className={`job-history__filter ${filter === 'accepted' ? 'job-history__filter--active' : ''}`}
            onClick={() => setFilter('accepted')}
          >
            Accepted
          </button>
          <button
            className={`job-history__filter ${filter === 'done' ? 'job-history__filter--active' : ''}`}
            onClick={() => setFilter('done')}
          >
            Done
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="job-history__loading">
          <div className="job-history__spinner"></div>
          <p>Loading job history...</p>
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="job-history__empty">
          <p>No jobs found for the selected filter.</p>
        </div>
      ) : (
        <div className="job-history__list">
          {filteredJobs.map((job) => (
            <div key={job.id} className="job-history__item">
              <div className="job-history__item-header">
                <div className="job-history__item-title">
                  <h3>{formatJobTitle(job, userRole, job.match)}</h3>
                  <span
                    className="job-history__status-badge"
                    style={{ backgroundColor: getJobStatusColor(job.status) }}
                  >
                    {getJobStatusLabel(job.status)}
                  </span>
                </div>
                {job.payment_status === 'paid' && job.payment_amount && (
                  <div className="job-history__payment">
                    <span className="job-history__payment-label">Payment:</span>
                    <span className="job-history__payment-amount">{formatCurrency(job.payment_amount)}</span>
                  </div>
                )}
              </div>

              <div className="job-history__item-details">
                <div className="job-history__detail">
                  <span className="job-history__detail-label">Date:</span>
                  <span className="job-history__detail-value">{formatDate(job.job_date)}</span>
                </div>
                <div className="job-history__detail">
                  <span className="job-history__detail-label">Time:</span>
                  <span className="job-history__detail-value">
                    {formatTime(job.start_time)} - {formatTime(job.end_time)}
                  </span>
                </div>
                <div className="job-history__detail">
                  <span className="job-history__detail-label">Hours:</span>
                  <span className="job-history__detail-value">{job.total_hours} hours</span>
                </div>
                {/* Only show distance for employees (not for employers viewing their own jobs) */}
                {userRole === 'employee' && job.match && job.match.distance_miles != null && typeof job.match.distance_miles !== 'undefined' && (
                  <div className="job-history__detail">
                    <span className="job-history__detail-label">Distance:</span>
                    <span className="job-history__detail-value">
                      {(() => {
                        const distance = typeof job.match.distance_miles === 'number' 
                          ? job.match.distance_miles 
                          : parseFloat(job.match.distance_miles);
                        return !isNaN(distance) ? distance.toFixed(1) : 'N/A';
                      })()} miles
                    </span>
                  </div>
                )}
                <div className="job-history__detail">
                  <span className="job-history__detail-label">Address:</span>
                  <span className="job-history__detail-value">{job.practice_address}</span>
                </div>
              </div>

              {job.payment_status && (
                <div className="job-history__payment-info">
                  <span className={`job-history__payment-status job-history__payment-status--${job.payment_status}`}>
                    {job.payment_status === 'paid' ? '✓ Paid' : '⏳ Pending Payment'}
                  </span>
                  {job.payment_date && (
                    <span className="job-history__payment-date">
                      Paid on {formatDate(job.payment_date)}
                    </span>
                  )}
                </div>
              )}

              {/* Delete button for employers (only for open/pending jobs) */}
              {userRole === 'employer' && canDeleteJob(job) && (
                <div className="job-history__item-actions">
                  <button
                    className="job-history__delete-button"
                    onClick={() => handleDeleteClick(job.id)}
                    disabled={deleteJobPostingMutation.isPending}
                  >
                    {deleteJobPostingMutation.isPending && deleteConfirmId === job.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="job-history__delete-modal-overlay" onClick={handleDeleteCancel}>
          <div className="job-history__delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="job-history__delete-modal-title">Delete Job Posting</h3>
            <p className="job-history__delete-modal-message">
              Are you sure you want to delete this job posting? This action cannot be undone.
            </p>
            <div className="job-history__delete-modal-actions">
              <button
                className="job-history__delete-modal-button job-history__delete-modal-button--cancel"
                onClick={handleDeleteCancel}
                disabled={deleteJobPostingMutation.isPending}
              >
                Cancel
              </button>
              <button
                className="job-history__delete-modal-button job-history__delete-modal-button--confirm"
                onClick={handleDeleteConfirm}
                disabled={deleteJobPostingMutation.isPending}
              >
                {deleteJobPostingMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobHistory;










